import { useState, useEffect } from "react";
import React from "react";
import {
  machineProfile,
  part,
  workOrder,
  vendor,
  customer,
  team,
  user,
  asset,
  workOrderCategory,
} from "../../api";
import { toast } from "react-toastify";
import {
  dateFormat,
  generateLocationToArray,
  getEmptyField,
  printLocation,
  renderImage,
} from "../../utils/helper";
import { QRCode } from "react-qrcode-logo";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { useDashboardContext } from "../../contexts/NewDashboardContext";
import LoadingIndicator from "../../components/LoadingIndicator";
import NormalInput from "../../components/form/HorizontalNormalInput";
import NormalSelect from "../../components/form/HorizontalNormalSelect";
import NormalTextArea from "../../components/form/HorizontalNormalTextarea";
import { DatepickerOpt } from "../../components/form/DatepickerOpt";
import DateTime from "react-datetime";
import "react-datetime/css/react-datetime.css";
import { Card, CardBody, CardHeader } from "../../components/card/card";
import { MachineElement } from "../../components/MachineElement";
import ImageViewer from "../../components/ImageViewer";
import { format, isBefore } from "date-fns";
import { Task } from "../../components/Task";
import { InventorySelector } from "../../components/InventorySelector";

function setupLocationValues(locationSeq, location) {
  locationSeq.push(location.id);
  if (location.parent_location) {
    setupLocationValues(locationSeq, location.parent_location);
  }
}

const recurringType = [
  {
    name: "Daily",
    id: "daily",
  },
  {
    name: "Weekly",
    id: "weekly",
  },
  {
    name: "Every Month",
    id: "monthly",
  },
  {
    name: "Every 3 Months",
    id: "3months",
  },
  {
    name: "Every 6 Months",
    id: "6months",
  },
  {
    name: "Every year",
    id: "yearly",
  },
];

const priorityType = [
  {
    name: "Low",
    id: "low",
  },
  {
    name: "Medium",
    id: "medium",
  },
  {
    name: "High",
    id: "high",
  },
];

function AddWorkOrder({ isRequest = false }) {
  const { id } = useParams();
  const editMode = !!id;

  const { locations, addSetupDone } = useDashboardContext();

  const [locationValue, setLocationValue] = useState([]);
  const [vendorList, setVendor] = useState([]);
  const [customerList, setCustomer] = useState([]);
  const [categories, setCategories] = useState([]);
  const [newCategory, setNewCategory] = useState("");
  const [schedules, setSchedules] = useState([]);
  const [assets, setAssets] = useState([]);
  const [sublocations, setSublocations] = useState([]);
  const { getLocationName, username } = useAuth();
  const [teams, setTeams] = useState([]);
  const [users, setUsers] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [assignType, setAssignType] = useState("individual");
  const [dataToSend, setDataToSend] = useState({
    asset_id: "",
    start_date: "",
    due_date: "",
    status: "",
    description: "",
    title: "",
    recurring_wo: "",
    priority: "medium",
    assigned_to: "",
    team_id: "",
    vendor_id: "",
    category_id: "",
    tasks: [],
    inventories: [],
    is_request: isRequest,
  });
  const navigate = useNavigate();
  function backToList() {
    navigate(-1);
  }

  const [preview, setPreview] = useState();
  const [selectedFile, setSelectedFile] = useState("");

  const [emptyField, setEmptyField] = useState([]);

  async function getMasterData() {
    setIsLoading(true);
    try {
      const getVendor = vendor.list({});

      const getTeam = team.list({});

      const getUser = user.list({});

      const getCustomer = customer.list({});

      const getCategory = workOrderCategory.list({});

      const [res, resTeam, resUser, resCust, resCategory] = await Promise.all([
        getVendor,
        getTeam,
        getUser,
        getCustomer,
        getCategory,
      ]);

      if (res.ok) {
        const body = await res.json();
        let data = body.data;
        if (data) {
          data.map((d) => {
            d["name"] = d.vendor_name;
            d["value"] = d.id;
          });
          setVendor(data);
        }
      }

      if (resTeam.ok) {
        const body = await resTeam.json();
        let data = body.data;
        if (data) {
          data.map((d) => {
            d["name"] = d.team_name;
            d["value"] = d.id;
          });
          setTeams(data);
        }
      }

      if (resUser.ok) {
        const body = await resUser.json();
        let data = body.data;
        if (data) {
          data.map((d) => {
            d["name"] = d.firstname + " " + d.lastname;
            d["value"] = d.username;
          });
          setUsers(data);
        }
      }
      if (resCust.ok) {
        const body = await resCust.json();
        let data = body.data;
        if (data) {
          data.map((d) => {
            d["name"] = d.customer_name;
            d["value"] = d.id;
          });
          setCustomer(data);
        }
      }
      if (resCategory.ok) {
        const body = await resCategory.json();
        let data = body.data;
        if (data) {
          data.map((d) => {
            d["name"] = d.name;
            d["value"] = d.id;
          });
          setCategories(data);
        }
      }

      if (editMode) {
        await getRecord();
      }

      setSublocations([locations]);
    } catch (err) {
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    if (locations.length) getMasterData();
  }, [locations]);

  const onSelectFile = async (e) => {
    if (!e.target.files || e.target.files.length === 0) {
      setSelectedFile(undefined);
      return;
    }

    // I've kept this example simple by using the first image instead of multiple
    setSelectedFile(e.target.files[0]);
  };

  useEffect(() => {
    if (!selectedFile) {
      setPreview(undefined);
      return;
    }
    //console.log({ selectedFile });
    const objectUrl = URL.createObjectURL(selectedFile);
    setPreview(objectUrl);
    //console.log({ objectUrl });
    // free memory when ever this component is unmounted
    return () => URL.revokeObjectURL(objectUrl);
  }, [selectedFile]);

  async function getRecord() {
    const result = await workOrder.getOne(id);

    if (result.ok) {
      const body = await result.json();
      let data = body.data;

      const defaultValue = {
        asset_id: data.asset_id,
        start_date: format(new Date(data.start_date), "yyyy-MM-dd"),
        due_date: format(new Date(data.due_date), "yyyy-MM-dd"),
        status: data.status,
        description: data.description,
        title: data.title,
        recurring_wo: data.recurring_wo,
        priority: data.priority,
        assigned_to: data.assigned_to,
        team_id: data.team_id || "",
        vendor_id: data.vendor_id,
        category_id: data.category_id,
        tasks: data.tasks || [],
        inventories: data.inventories || [],
        is_request: isRequest,
      };

      if (data.asset?.location) {
        //find parent
        const locationSeq = [];
        setupLocationValues(locationSeq, data.asset.location);
        locationSeq.reverse();
        setLocationValue(locationSeq);
      }

      if (data.attachments?.photo) {
        if (
          data.attachments?.photo.includes("http") ||
          data.attachments?.photo.includes("https")
        ) {
          setPreview(data.attachments?.photo);
        } else {
          setPreview(
            renderImage(`/machine/attachment/${data.attachments?.photo}`)
          );
        }
      }

      setAssignType(data.team_id ? "team" : "individual");
      setDataToSend(defaultValue);
    }
  }

  const handleAddNewCategory = async () => {
    if (!newCategory) return;
    const result = await workOrderCategory.create({
      name: newCategory,
    });

    if (result.ok) {
      const body = await result.json();
      const data = body.data;
      setCategories((prev) => [{ ...data }, ...prev]);
      setNewCategory("");
    }
  };

  const handleOnchange = (e) => {
    let key = e.target.name;

    setDataToSend((prevData) => ({
      ...prevData,
      [key]: e.target.value,
    }));
  };

  const handleSubmit = async () => {
    setIsLoading(true);
    try {
      let payload = { ...dataToSend };
      if (payload.inventories && payload.inventories.length > 0) {
        if (payload.inventories.some((p) => p.inventory_id == '')) {
          toast.error("Some of the part is not selected!");
          return;
        }
      }
      payload["tasks"] = JSON.stringify(payload.tasks);
      payload["inventories"] = JSON.stringify(payload.inventories);
      payload["assignType"] = assignType;
      const mandatory = [
        "asset_id",
        "start_date",
        "due_date",
        "title",
        "description",
        "asset_id",
        "category_id",
        "priority",
      ];
      if (assignType == "individual") mandatory.push("assigned_to");
      else mandatory.push("team_id");
      const ef = getEmptyField(payload, mandatory);
      setEmptyField(ef);
      if (ef.length > 0) {
        toast.error("Mandatory field must be filled");
        return;
      }

     

      //console.log({ payload });
      let res;
      if (!editMode) {
        res = await workOrder.create(payload, selectedFile);
        await addSetupDone({ user_setup_center: "work order" });
        if (dataToSend.assigned_to && dataToSend.assigned_to !== username) {
          await addSetupDone({ user_setup_center: "assign" });
        }
      } else {
        res = await workOrder.update(id, payload, selectedFile);
        if (dataToSend.assigned_to && dataToSend.assigned_to !== username) {
          await addSetupDone({ user_setup_center: "assign" });
        }
      }

      if (res.ok) {
        if (isRequest) {
          toast.success(`Your request has been ${editMode?'updated':'submitted'}`);
        } else {
          toast.success(`Your work order has been ${editMode?'updated':'created'}`);
        }

        if (!editMode) {
          backToList();
        }
      } else {
        toast.error("Something went wrong, please try again");
      }
    } catch (e) {
      toast.error(e.message);
      console.error(e);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    let sublocations = locations;
    const copy = [sublocations];

    const findSublocation = (lv, index) => {
      const location = sublocations.find((l) => l.id == lv);

      if (
        location &&
        location.sublocations &&
        location.sublocations.length > 0
      ) {
        copy.splice(index + 1);
        copy.push(location.sublocations);
        sublocations = location.sublocations;
      } else {
        copy.splice(index + 1);
      }
    };

    if (locationValue.length > 0) {
      locationValue.forEach((lv, index) => {
        findSublocation(lv, index);
      });
      setSublocations(copy);
    }
  }, [locationValue]);

  const handleLocationChange = (e, index) => {
    const location_values = [...locationValue];
    location_values.splice(index);
    location_values.push(e.target.value);
    console.log(location_values);
    setLocationValue(location_values);
  };

  const getAsset = async (location_id) => {
    const res = await asset.list({ location_id });
    if (res.ok) {
      const body = await res.json();
      let data = body.data;
      if (data) {
        data.map((d) => {
          d["name"] = d.asset_name;
          d["value"] = d.id;
        });
        setAssets(data);
      }
    }
  };

  useEffect(() => {
    if (locationValue.length > 0) {
      const location_id = locationValue[locationValue.length - 1];
      getAsset(location_id);
    }
  }, [locationValue]);

  useEffect(() => {

    if (dataToSend.due_date) {
      if (isBefore(new Date(dataToSend.due_date), new Date(dataToSend.start_date))) {
        setDataToSend({
          ...dataToSend,
          due_date:''
        })
      }
    }

  },[dataToSend.start_date])
  return (
    <Card>
      <LoadingIndicator open={isLoading} />
      <CardHeader>
        {editMode && "Edit "}
        {isRequest ? "Request" : "Work Order"} Information
      </CardHeader>

      <CardBody>
        <div className="row g-3">
          <div className="col-md-6">
            <NormalInput
              label={`${isRequest ? "Request" : "Work Order"} Title`}
              value={dataToSend["title"]}
              onchange={handleOnchange}
              name={"title"}
              type="text"
              id="workOrderTitle"
              emptyField={emptyField}
              className="form-control"
              required
            />

            <NormalInput
              label={"Start Date"}
              type="date"
              onchange={handleOnchange}
              value={dataToSend["start_date"]}
              name={"start_date"}
              id="startDate"
              emptyField={emptyField}
              className="form-control"
              required

            />

            <NormalInput
              label={"Due Date"}
              onchange={handleOnchange}
              value={dataToSend["due_date"]}
              min={dataToSend["start_date"]}
              name={"due_date"}
              type="date"
              id="dueDate"
              emptyField={emptyField}
              className="form-control"
              required
            />

            {!isRequest && (
              <NormalSelect
                label={"Recurring WO"}
                onchange={handleOnchange}
                value={dataToSend["recurring_wo"]}
                name={"recurring_wo"}
                id="recurringWO"
                className="form-select"
                emptyField={emptyField}
                valueKey={"id"}
                list={recurringType}
              />
            )}

            <NormalTextArea
              label={"Description"}
              onchange={handleOnchange}
              value={dataToSend["description"]}
              name={"description"}
              type="text"
              id="description"
              className="form-control"
              emptyField={emptyField}
              required
            />

            {sublocations.map((sublocation, index) => {
              return (
                <NormalSelect
                  key={index}
                  required={true}
                  value={locationValue[index]}
                  label={getLocationName(index)}
                  onchange={(e) => handleLocationChange(e, index)}
                  list={sublocation}
                  name={`location_id`}
                  valueKey={"id"}
                  textKey="location_name"
                  renderFn={printLocation}
                  emptyField={emptyField}
                />
              );
            })}

            <NormalSelect
              label={"Asset"}
              onchange={handleOnchange}
              value={dataToSend["asset_id"]}
              name={"asset_id"}
              id="asset"
              className="form-select"
              required
              list={assets}
              valueKey={"id"}
              emptyField={emptyField}
            />
          </div>

          <div className="col-md-6">
            <NormalSelect
              label={"Priority"}
              onchange={handleOnchange}
              value={dataToSend["priority"]}
              name={"priority"}
              id="priority"
              className="form-select"
              valueKey={"id"}
              list={priorityType}
              emptyField={emptyField}
              required
            />

            <NormalSelect
              required={true}
              value={dataToSend["category_id"]}
              label="Category"
              onchange={handleOnchange}
              list={categories}
              name="category_id"
              emptyField={emptyField}
              valueKey={"id"}
              onSubmit={handleAddNewCategory}
              addMoreComponent={
                <NormalInput
                  required={true}
                  value={newCategory}
                  label="Category"
                  onchange={(e) => {
                    setNewCategory(e.target.value);
                  }}
                />
              }
            />

            <div className="mb-3">
              <label htmlFor="assignee" className="form-label">
                Assignee
              </label>
              <select
                id="assignee"
                className="form-select"
                onChange={(e) => {
                  setAssignType(e.target.value);
                }}
                value={assignType}
                required
              >
                <option value="individual">Individual</option>
                <option value="team">Team</option>
              </select>
            </div>
            {assignType === "individual" && (
              <NormalSelect
                label={"Assigned to"}
                id="assignee"
                className="form-select"
                onchange={handleOnchange}
                value={dataToSend["assigned_to"]}
                name={"assigned_to"}
                required
                emptyField={emptyField}
                list={users}
                valueKey={"value"}
              />
            )}
            {assignType === "team" && (
              <NormalSelect
                label={"Assigned to"}
                id="assignee"
                className="form-select"
                onchange={handleOnchange}
                value={dataToSend["team_id"]}
                name={"team_id"}
                required
                emptyField={emptyField}
                valueKey={"value"}
                list={teams}
              />
            )}
          </div>
        </div>
        <div className="accordion" id="accordion2" style={{marginBottom:"10px"}}>
          <div className="accordion-item">
            <h2 className="accordion-header" id="headingChat">
              <button
                  className="accordion-button"
                  type="button"
                  data-bs-toggle="collapse"
                  style={{ padding: "5px" }}
                  data-bs-target="#collapseChat"
              >
                <img
                    src="/assets/img/ai_face.png"
                    style={{ width: "35px", marginRight: "10px" }}
                    alt=""
                />
                Automatically Generate Task List
              </button>
            </h2>
            <div
                id="collapseChat"
                className="accordion-collapse collapse"
                data-bs-parent="#accordionExample"
            >
              <div className="accordion-body">
               <div>Our AI assistant helps you create tasks automatically, saving you time and effort. It intelligently generates task lists based on your filled information, ensuring nothing is missed. </div>
                <button
                    className="btn btn-sm btn-outline-theme"
                    style={{ marginRight: "10px", marginTop: "10px" }}
                >
                 <i className="fa fa-refresh"></i> Auto Generate Task
                </button>
              </div>
            </div>
          </div>
        </div>

        {(!isRequest ||
          (isRequest && editMode)) && (
            <Task
              showAddChecklist
              onChange={(v) =>
                handleOnchange({
                  target: {
                    name: "tasks",
                    value: v,
                  },
                })
              }
              value={dataToSend["tasks"]}
            />
          )}

        {!isRequest && (
          <div className="mt-2">
            <InventorySelector
              showAddChecklist
              onChange={(v) =>
                handleOnchange({
                  target: {
                    name: "inventories",
                    value: v,
                  },
                })
              }
              value={dataToSend["inventories"]}
            />
          </div>
        )}

        <Card style={{ marginTop: "10px" }}>
          <CardBody>
            <label htmlFor="files">
              {preview ? (
                <>
                  <img src={preview} className="img-thumbnail" alt="" />
                  <div
                    style={{
                      textAlign: "center",
                      cursor: "pointer",
                      marginTop: "5px",
                    }}
                  >
                    Change Photo <i className="fa fa-camera"></i>
                  </div>
                </>
              ) : (
                <>
                  <div
                    style={{
                      cursor: "pointer",
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                      minHeight: "150px",
                      alignItems: "center",
                      transform: "translate(-50%, -50%)",
                    }}
                  >
                    <i className="fa fa-camera"></i>
                    <div style={{ textAlign: "center" }}>Add a Photo</div>
                  </div>
                </>
              )}
            </label>
            <input
              id="files"
              onChange={onSelectFile}
              style={{ visibility: "hidden", position: "absolute" }}
              type="file"
              accept="image/png, image/jpeg"
            />
          </CardBody>
        </Card>

        <Card style={{ marginTop: "10px" }}>
          <LoadingIndicator open={isLoading} />
          <CardHeader>More Information</CardHeader>
          <CardBody>
            <NormalSelect
              value={dataToSend["vendor_id"]}
              label="Vendor"
              onchange={handleOnchange}
              list={vendorList}
              valueKey="id"
              name="vendor_id"
            />
          </CardBody>
        </Card>

        {!isRequest && (
          <div className="mt-4 flex">
            <button
              className="btn btn-outline-theme btn-lg me-2"
              onClick={handleSubmit}
            >
              {editMode ? "SAVE" : "ADD"}
            </button>
            <button
              className="btn btn-outline-danger btn-lg"
              onClick={backToList}
            >
              CANCEL
            </button>
          </div>
        )}
        {isRequest && (
          <div className="mt-4 flex">
            <button
              className="btn btn-outline-theme btn-lg me-2"
              onClick={handleSubmit}
            >
              {editMode ? "SAVE REQUEST" : "SUBMIT REQUEST"}
            </button>
            <button
              className="btn btn-outline-danger btn-lg"
              onClick={backToList}
            >
              CANCEL
            </button>
          </div>
        )}
      </CardBody>
    </Card>
  );
}

export default AddWorkOrder;
